// src/components/common/Modal.js
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import Tooltip from './Tooltip';
import { CloseButton1 } from '../Templates';
import './Modal.css';

/**
 * A reusable modal component that renders content in a popup window.
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onClose - Function to call when modal is closed
 * @param {string} [props.title] - Optional title for the modal
 * @param {React.ReactNode} props.children - Content to display in the modal
 * @param {string} [props.modalSize='medium'] - Size of the modal: 'small', 'medium', 'large', or 'fullscreen'
 * @param {boolean} [props.closeOnEscape=true] - Whether to close modal when Escape key is pressed
 * @param {boolean} [props.closeOnOverlayClick=true] - Whether to close modal when clicking overlay
 */
const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  modalSize = 'medium',
  closeOnEscape = true,
  closeOnOverlayClick = true,
}) => {
  const { t } = useTranslation('common');
  const modalRef = useRef(null);

  // Handle escape key press
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, closeOnEscape]);

  // Handle modal click outside
  const handleOverlayClick = (e) => {
    if (closeOnOverlayClick && modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  // Prevent scrolling of the body when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  // Don't render anything if modal is closed
  if (!isOpen) return null;

  // Map modalSize to CSS class name
  const sizeClass = {
    small: 'modal-small',
    medium: 'modal-medium',
    large: 'modal-large',
    fullscreen: 'modal-fullscreen',
  }[modalSize] || 'modal-medium';

  // Use createPortal to render modal at the end of the document body
  return createPortal(
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className={`modal-container ${sizeClass}`} ref={modalRef}>
        <div className="modal-header">
          {title && <h2 className="modal-title">{title}</h2>}
          <Tooltip text={t('buttons.close') || 'Close'} position="bottom">
            <CloseButton1 onClick={onClose} className="modal-close-btn" />
          </Tooltip>
        </div>
        <div className="modal-content">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Modal;